import { Icon, ImageFallback, Shadow, Text, HStack, VStack } from '@genial/components';
import { BoxRadius, Color, Spacing, Typography } from '@genial/design-tokens';
import { IconographyTypes } from '@genial/icons';
import { Utils } from '@genial/utils';
import { Box, FlatList, Skeleton } from 'native-base';
import React from 'react';

import { TableContentItemButton } from '../../../../../../../../../../components/TableContent/button';
import { flatListProps } from '../../../../../../../../utils';
import { Separator } from '../../../../../../../Separator';
import { data } from '../../data';

interface ItemProps {
  simulatedValue: number;
  itemName: string;
  categoryIcon: string;
}

interface ListPlansProps {
  params: ItemProps;
  onPress: (item: (typeof data)[number]) => void;
  isLoading: boolean;
}

const Loading = () => (
  <VStack
    space={Spacing['stack.xxxs']}
    bg={Utils.ColorToken.WithOpacity('brand.tertiary.lightest', 0.3)}
    px={Spacing['inline.xxs']}
    py={Spacing['inline.xxxs']}
    borderRadius={BoxRadius.sm}>
    <HStack alignItems="center" justifyContent="space-between">
      <Skeleton
        w="35%"
        h={Typography['fontsize.display']}
        borderRadius={BoxRadius.sm}
        endColor={Color['brand.primary.lightest']}
      />
      <Skeleton
        w="25%"
        h={Typography['fontsize.xl']}
        borderRadius={BoxRadius.sm}
        endColor={Color['brand.primary.lightest']}
      />
    </HStack>
    <Box h="1px" w="full" bg="neutral.lightest" />
    <HStack space={Spacing['inline.xxxs']}>
      <VStack space={Spacing['inline.quarck']} flex={1}>
        <Skeleton
          w="65%"
          h={Typography['fontsize.lg']}
          borderRadius={BoxRadius.sm}
          endColor={Color['brand.primary.lightest']}
        />
        <Skeleton
          w="90%"
          h={Typography['fontsize.lg']}
          borderRadius={BoxRadius.sm}
          endColor={Color['brand.primary.lightest']}
        />
      </VStack>
      <VStack space={Spacing['inline.quarck']} alignItems="flex-end" flex={1}>
        <Skeleton
          w="60%"
          h={Typography['fontsize.lg']}
          borderRadius={BoxRadius.sm}
          endColor={Color['brand.primary.lightest']}
        />
        <Skeleton
          w="40%"
          h={Typography['fontsize.lg']}
          borderRadius={BoxRadius.sm}
          endColor={Color['brand.primary.lightest']}
        />
      </VStack>
    </HStack>
  </VStack>
);

export const ListPlans: React.FC<ListPlansProps> = ({ params, isLoading, onPress }) => {
  return (
    <Shadow level={1} borderRadius={BoxRadius.sm}>
      <VStack p={Spacing['stack.xxs']} space={Spacing['stack.xxxs']} borderRadius={BoxRadius.sm}>
        <HStack alignItems="center" justifyContent="space-between">
          {isLoading ? (
            <Skeleton
              w="45%"
              h={Typography['fontsize.xxl']}
              borderRadius={BoxRadius.sm}
              endColor={Color['brand.primary.lightest']}
            />
          ) : (
            <HStack space={Spacing['inline.nano']} alignItems="center">
              <Icon
                name={params.categoryIcon as IconographyTypes.Option}
                size="sm"
                color={Color['brand.tertiary.principal']}
              />
              <Text variant="semibold" fontSize={Typography['fontsize.lg']} color="neutral.black">
                {params.itemName}
              </Text>
            </HStack>
          )}
          {isLoading ? (
            <Skeleton
              w="25%"
              h={Typography['fontsize.xl']}
              borderRadius={BoxRadius.sm}
              endColor={Color['brand.primary.lightest']}
            />
          ) : (
            <Text variant="semibold" fontSize={Typography['fontsize.lg']} color="neutral.black">
              {Utils.Mask.Money(params.simulatedValue)}
            </Text>
          )}
        </HStack>
        {isLoading ? (
          <VStack space={Spacing['stack.xxxs']}>
            {Array.from({ length: 4 }).map((_, index) => (
              <Loading key={index.toString()} />
            ))}
          </VStack>
        ) : (
          <FlatList
            {...flatListProps}
            maxH="580px"
            data={data}
            ItemSeparatorComponent={Separator}
            keyExtractor={(item) => item.id}
            renderItem={({ item }) => (
              <VStack
                space={Spacing['stack.xxxs']}
                bg={Utils.ColorToken.WithOpacity('brand.tertiary.lightest', 0.3)}
                px={Spacing['inline.xxs']}
                py={Spacing['inline.xxxs']}
                borderRadius={BoxRadius.sm}>
                <HStack alignItems="center" space={Spacing['inset.quarck']}>
                  <ImageFallback src="" alt="image-consortium" width="30px" height="30px" />
                  <Text color="neutral.medium" flex={1}>
                    {item.company}
                  </Text>
                  <TableContentItemButton forceDesktop onPress={() => onPress(item)} />
                </HStack>
                <Box h="1px" w="full" bg="neutral.lightest" />
                <HStack space={Spacing['inline.xxxs']}>
                  <VStack space={Spacing['inline.quarck']} flex={1}>
                    <Text>{Utils.Mask.Money(item.creditValue)}</Text>
                    <Text color="neutral.medium">Taxa de administração</Text>
                  </VStack>
                  <VStack space={Spacing['inline.quarck']} alignItems="flex-end">
                    <HStack alignItems="center" space={Spacing['inline.quarck']}>
                      <Text color="neutral.medium">{item.installments} x</Text>
                      <Text variant="semibold" color="neutral.black">
                        {Utils.Mask.Money(item.installmentValue)}
                      </Text>
                    </HStack>
                    <Text color="neutral.medium">{Utils.Mask.Percentage(item.adminFee)}</Text>
                  </VStack>
                </HStack>
              </VStack>
            )}
          />
        )}
      </VStack>
    </Shadow>
  );
};
